import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import cancelOrder from '@/shared/api/orders/cancelOrder';
import getOrders from '@/shared/api/orders/getOrders';
import getQuote from '@/shared/api/orders/quotes/getQuote';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import DialogName from '@/shared/types/dialogName.enum';
import Order from '@/shared/types/order/order.type';
import QuoteResponse from '@/shared/types/order/quotes/quoteResponse.type';

import UpdateHandlers from '../../types/updateHandlers.type';

type Args = {
  dialogProps: UpdateHandlers<unknown>;
};

const useOrder = ({ dialogProps }: Args) => {
  const { t } = useTranslation(['checkout']);
  const redirectLogin = useRedirectLogin();
  const orderId = useParams().id;

  const [order, setOrder] = useState<Order | null>(null);
  const [orderQuote, setOrderQuote] = useState<QuoteResponse | null>(null);
  const [amountOwed, setAmountOwed] = useState(0);
  const [totalCostInfo, setTotalCostInfo] = useState({
    order_total: '0',
    shipping_price: '0',
    sub_total: '0',
    tax: '0',
    discount: '',
  });
  const [refetchOrder, setRefetchOrder] = useState(false);

  const handleCloseOrder = async (isCloseWithRefund: boolean) => {
    if (!orderId) {
      console.error('No order id provided');
      return;
    }

    dialogProps.onUpdateDialogState(DialogName.CancelOrder, {
      loading: true,
    });

    const response = await cancelOrder({
      orderId,
      body: {
        refund_order: isCloseWithRefund,
      },
      onUnauthorized: redirectLogin,
    });

    dialogProps.onUpdateDialogState(DialogName.CancelOrder, {
      loading: false,
    });

    if (response.success) {
      dialogProps.onUpdateDialogState(DialogName.CancelOrder, {
        open: false,
      });
      dialogProps.onUpdateNotificationBar({
        open: true,
        message: t('checkout:orderCancelledSuccess'),
      });
      setRefetchOrder(true);
    } else {
      dialogProps.onUpdateDialogState(DialogName.CancelOrder, {
        errors: [response.msg],
      });
    }
  };

  useEffect(() => {
    if (orderId) {
      setRefetchOrder(false);

      const getOrderAndQuote = async () => {
        const getOrdersPromise = await getOrders(
          {
            search: [
              {
                in_order_id: {
                  cop: ['='],
                  value: orderId,
                },
              },
            ],
          },
          redirectLogin,
        );

        if (getOrdersPromise.success) {
          const orderItem = getOrdersPromise.data[0];

          setOrder(orderItem);
          setAmountOwed(
            parseFloat(orderItem.order_total) -
              parseFloat(
                orderItem.payments
                  .reduce(
                    (acc, curr) => acc + parseFloat(curr.transaction_amount),
                    0,
                  )
                  .toFixed(2),
              ),
          );

          const getQuotePromise = await getQuote({
            quoteId: getOrdersPromise.data[0].order_quote_id,
            onUnauthorized: redirectLogin,
          });

          if (getQuotePromise.success) {
            setOrderQuote(getQuotePromise.data);

            setTotalCostInfo({
              order_total: getQuotePromise.data.order_total,
              shipping_price: getQuotePromise.data.shipping_price,
              sub_total: getQuotePromise.data.sub_total,
              tax: getQuotePromise.data.tax,
              discount: getQuotePromise.data.discount,
            });
          }
        }
      };

      void getOrderAndQuote();
    }
  }, [orderId, refetchOrder, redirectLogin]);

  return {
    order,
    orderQuote,
    amountOwed,
    totalCostInfo,
    setRefetchOrder,
    handleCloseOrder,
  };
};

export default useOrder;
